import { isNull } from "../../../../lib/html5-software-library/src/ranj/util/TypeUtil";
import { getURLParameter } from "../../../../lib/html5-software-library/src/ranj/util/URLUtil";
import AppModel from "../../app/core/AppModel";
import ISaveModel from "../savegame/ISaveModel";
import PlatformAPIEventType from "./PlatformAPIEventType";
import PlatformParameters from "./PlatformParameters";

export default class PlatformAPIService {

    /**
     * @param {AppModel} appModel 
     * @param {ISaveModel} saveModel 
     */
    constructor(appModel, saveModel) {
        this._appModel = appModel;
        this._saveModel = saveModel;
        this._loggingEnabled = false;
    }

    /**
     * @param {string} version 
     * @returns {Promise<PlatformParameters>}
     */
    initialize(version) {
        this._version = version;

        this._api = discoverAPI(window);
        if (isNull(this._api)) {
            throw new Error("Platform API not found in parent or opener windows.");
        }

        if (this.supportsGetConfigurationAsync()) {
            return this.getConfigurationAsync();
        } else {
            const platformParameters = this.getConfigurationSync();
            return Promise.resolve(platformParameters);
        }
    }

    /**
     * Enable logging of sent events in console.
     * @param {boolean} enable Whether console logs should be enabled.
     */
    enableLogging(enable) {
        this._loggingEnabled = enable;
    }

    exit() {
        this._api.exit();
    }

    /**
     * Gets the configuration from the platform API in a sync manner.
     * This method is obsolete and only there for backwards compatibility. The new way is to use 'getConfigurationAsync'.
     * @deprecated NOTE: EQ has not implemented 'getConfigurationAsync' as of 11 jan 2023 yet so this method should be supported still.
     * @returns {PlatformParameters}
     */
    getConfigurationSync() {
        console.log("PlatformAPI.getConfiguration, api method exists: " + (this._api.getConfiguration != null));
        const params = PlatformParameters.fromObject(this._api.getConfiguration());
        this._applyPlatformConfiguration(params)

        return params;
    }

    supportsGetConfigurationAsync() {
        return this._api.getConfigurationAsync != null;
    }

    /**
     * Gets the configuration from the platform API in an async manner.
     * @returns {PlatformParameters}
     */
    getConfigurationAsync() {
        const getConfigurationAsyncIsAvailable = this.supportsGetConfigurationAsync();
        console.log("PlatformAPI.getConfigurationAsync, api method exists: " + getConfigurationAsyncIsAvailable);
        if (!getConfigurationAsyncIsAvailable) {
            throw (`PlatformAPI.getConfigurationAsync is not available`);
        }

        return this._api.getConfigurationAsync()
            .then(response => {
                const params = PlatformParameters.fromObject(response);
                this._applyPlatformConfiguration(params);
            });
    }

    // PoC Async Events
    sendEventAsync(type, payload) {
        return this._api.sendEventAsync(type, payload);
    }

    // PoC Async Event
    sendGameInterrupted(payload) {
        return this._api.gameInterrupted(payload);
    }

    sendInteractionEvent(eventName, eventParams, elapsedTime) {
        return this.sendEvent(PlatformAPIEventType.INTERACTION, {
            event_name: eventName,
            event_params: eventParams,
            elapsed_time: elapsedTime
        });
    }

    sendProgressEvent(progressValue, elapsedTime) {
        return this.sendEvent(PlatformAPIEventType.PROGRESS, {
            progress_value: progressValue,
            elapsed_time: elapsedTime
        });
    }

    save(stateObj) {
        return this.sendEvent(PlatformAPIEventType.SAVE_REQUEST, {
            interaction_state: JSON.stringify(stateObj)
        });
    }

    /**
     * Adds missing generic parameters to payload and sends event
     * @param {string} eventType - See PlatformAPIEventType
     * @param {Object} payload 
     */
    sendEvent(eventType, payload) {
        if (isNull(payload.timestamp)) {
            payload.timestamp = new Date().toJSON();
        }
        if (isNull(payload.version)) {
            payload.version = this._version;
        }
        if (this._loggingEnabled) {
            console.log("PlatformAPI.sendEvent", eventType, payload);
        }
        return this._api.sendEvent(eventType, payload);
    }

    sendTestEventAsync(progressValue, elapsedTime) {
        return this.sendEvent(PlatformAPIEventType.PROGRESS, {
            progress_value: progressValue,
            elapsed_time: elapsedTime
        });
    }

    /**
     * Applies platform configuration/ parameters to game models.
     * @param {PlatformParameters} platformParameters 
     */
    _applyPlatformConfiguration(platformParameters) {
        this._appModel.language = DEBUG_LANGUAGE || platformParameters.language;
        this._appModel.playMode = DEBUG_PLAYMODE || platformParameters.playMode;
        this._appModel.environment = platformParameters.environment;
        this._saveModel.setSaveGame(getURLParameter("clear") == "1" ? null : platformParameters.state);
    }
}


/**
 * Discover API object in the opener/parent window.
 * @param {Window} win 
 * @returns 
 */
const discoverAPI = function (win) {
    var scan = function (win) {
        while ((win[API_NAME] == null) && (win.parent != null) && (win.parent != win)) {
            win = win.parent;
        }
        return win[API_NAME];
    }
    //search either in parent or in opener
    let api;
    // iframes dont have window.opener, in that case try window.parent instead
    if (win.parent != null && win.parent != win) {
        api = scan(win.parent);
    }
    if (api == null && win.opener != null) {
        api = scan(win.opener);
    }
    return api;
}

const API_NAME = "platformAPI";

const DEBUG_PLAYMODE = getURLParameter("mode");
const DEBUG_LANGUAGE = getURLParameter("lang");